import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { PartyPlatterCustomizer } from "@/components/PartyPlatterCustomizer";
import { PlatterDetails } from "@/components/PlatterDetails";
import { useStoreMenu, useUnavailableLines } from "@/lib/storeMenu";
import { useState } from "react";

export default function Cart() {
  const { items, updateQty, updatePlatter, removeItem, subtotal, storeId } = useCart();
  const nav = useNavigate();
  const [editing, setEditing] = useState(null);
  const { items: storeMenu, loading: menuLoading } = useStoreMenu(storeId);
  const unavailable = useUnavailableLines(items, storeMenu, menuLoading);
  const blockedIds = new Set(unavailable.map((l) => l.line_id));
  const tax = subtotal * 0.08;
  const total = subtotal + tax;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
      <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight mb-10" data-testid="cart-title">Your Cart</h1>

      {items.length === 0 ? (
        <div className="text-center py-24 border-2 border-dashed border-neutral-200 rounded-3xl" data-testid="cart-empty">
          <ShoppingBag className="w-10 h-10 mx-auto text-neutral-300 mb-4"/>
          <p className="text-neutral-500 mb-6">Your cart is empty.</p>
          <Link to="/menu"><Button className="rounded-full bg-[hsl(var(--primary))]" data-testid="cart-browse-menu">Browse Menu</Button></Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {items.map((it) => (
              <div
                key={it.line_id}
                className="flex flex-col gap-4 rounded-2xl border border-neutral-200/70 bg-white p-4 sm:flex-row sm:items-center"
                data-testid={`cart-item-${it.line_id}`}
              >
                <img src={it.image_url} alt={it.name} className="w-20 h-20 rounded-xl object-cover"/>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-semibold truncate">{it.name}</h3>
                  <p className="text-[hsl(var(--primary))] font-semibold">${it.price.toFixed(2)}</p>
                  <PlatterDetails platter={it.platter} compact />
                  {blockedIds.has(it.line_id) && (
                    <p className="mt-1 text-xs font-semibold text-red-600" data-testid={`cart-unavailable-${it.line_id}`}>
                      Unavailable at your pickup location
                    </p>
                  )}
                  {it.platter && (
                    <button
                      type="button"
                      onClick={() => setEditing(it)}
                      className="mt-2 text-xs font-semibold text-[hsl(var(--primary))] underline"
                      data-testid={`edit-platter-${it.line_id}`}
                    >
                      Edit platter choices
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between gap-3 sm:justify-start">
                  <div className="flex items-center gap-2 border border-neutral-200 rounded-full">
                    <button onClick={() => updateQty(it.line_id, it.quantity - 1)} className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100 rounded-full" data-testid={`decr-${it.line_id}`}><Minus className="w-3 h-3"/></button>
                    <span className="w-8 text-center text-sm font-semibold" data-testid={`qty-${it.line_id}`}>{it.quantity}</span>
                    <button onClick={() => updateQty(it.line_id, it.quantity + 1)} className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100 rounded-full" data-testid={`incr-${it.line_id}`}><Plus className="w-3 h-3"/></button>
                  </div>
                  <button onClick={() => removeItem(it.line_id)} className="text-neutral-400 hover:text-red-600 p-2" data-testid={`remove-${it.line_id}`}><Trash2 className="w-4 h-4"/></button>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white p-6 rounded-2xl border border-neutral-200/70 h-fit sticky top-24">
            <h2 className="font-display font-semibold text-xl mb-4">Order Summary</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Tax (8%)</span><span>${tax.toFixed(2)}</span></div>
              <div className="border-t border-neutral-200 my-3"/>
              <div className="flex justify-between font-display font-bold text-lg"><span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span></div>
            </div>
            <Button onClick={() => nav("/checkout")} className="w-full mt-6 rounded-full h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="checkout-btn">
              Proceed to Checkout
            </Button>
            <p className="text-xs text-neutral-500 mt-3 text-center">Secure card payment at checkout</p>
          </div>
        </div>
      )}
      {editing && (
        <PartyPlatterCustomizer
          item={{
            id: editing.item_id,
            name: editing.name,
            price: editing.price - editing.platter.extra_total,
            image_url: editing.image_url,
            platter_config: editing.platter.config,
          }}
          open={!!editing}
          initialPlatter={editing.platter}
          submitLabel="Save changes"
          onClose={() => setEditing(null)}
          onSave={(platter) => {
            updatePlatter(editing.line_id, {
              ...editing,
              price: editing.price - editing.platter.extra_total,
            }, { ...platter, config: editing.platter.config });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
